import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ChurchList from './components/ChurchList';
import ChurchModal from './components/ChurchModal';
import Stats from './components/Stats';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [churches, setChurches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    state: '',
    denomination: '',
    hasEmail: '',
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState(null);

  // Fetch churches
  useEffect(() => {
    fetchChurches();
  }, [filters]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchChurches = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/churches`, {
        params: filters,
      });
      setChurches(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch churches');
      console.error('Error fetching churches:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : value, // Reset to page 1 on filter change
    }));
  };

  const handleChurchClick = async (church) => {
    try {
      const response = await axios.get(`${API_URL}/churches/${church.id}`);
      setSelectedChurch(response.data.data);
    } catch (err) {
      console.error('Error fetching church details:', err);
      setError('Failed to load church details');
    }
  };

  const handleCloseModal = () => {
    setSelectedChurch(null);
  };

  const handleAddContact = async (churchId, contactData) => {
    try {
      await axios.post(`${API_URL}/churches/${churchId}/contact`, contactData);
      // Refresh church details
      const response = await axios.get(`${API_URL}/churches/${churchId}`);
      setSelectedChurch(response.data.data);
      fetchStats(); // Update stats
    } catch (err) {
      console.error('Error adding contact:', err);
      alert('Failed to add contact history');
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>🏛️ Church CRM Dashboard</h1>
        <p>Manage your church directory and outreach campaigns</p>
      </header>

      <main className="container">
        {/* Statistics */}
        {stats && <Stats stats={stats} />}

        {/* Filters */}
        <div className="filters">
          <h2>Search & Filter</h2>
          <div className="filter-row">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Name, city, or denomination..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>State</label>
              <input
                type="text"
                placeholder="e.g., TX"
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
                maxLength={2}
              />
            </div>
            <div className="filter-group">
              <label>Denomination</label>
              <input
                type="text"
                placeholder="e.g., Baptist"
                value={filters.denomination}
                onChange={(e) => handleFilterChange('denomination', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Has Email</label>
              <select
                value={filters.hasEmail}
                onChange={(e) => handleFilterChange('hasEmail', e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && <div className="error">{error}</div>}

        {/* Church List */}
        <ChurchList
          churches={churches}
          loading={loading}
          pagination={pagination}
          onChurchClick={handleChurchClick}
          onPageChange={(page) => handleFilterChange('page', page)}
        />

        {/* Church Detail Modal */}
        {selectedChurch && (
          <ChurchModal
            church={selectedChurch}
            onClose={handleCloseModal}
            onAddContact={handleAddContact}
          />
        )}
      </main>
    </div>
  );
}

export default App;
